import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Card, CardContent, Grid,
  TextField, MenuItem, LinearProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import { Savings, Add, CheckCircle, Cancel, Close, Paid } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  pending:   { color: 'warning', label: '⏳ Pending' },
  approved:  { color: 'info',    label: '✅ Approved' },
  rejected:  { color: 'error',   label: '❌ Rejected' },
  paid:      { color: 'success', label: '💰 Paid' },
  cancelled: { color: 'default', label: '🚫 Cancelled' },
};

const fmtHKD = (n?: number) => n != null ? `HK$${Number(n).toLocaleString('en-HK', { minimumFractionDigits: 2 })}` : '—';

interface Encashment { id: string; employeeId: string; leaveTypeId: string; leaveTypeName: string; year: number; daysRequested: number; dailyRate?: number; totalAmount?: number; currency: string; status: string; reason?: string; approverNote?: string; paidDate?: string; employee?: { firstName: string; lastName: string; department?: string }; createdAt: string; }
interface LeaveType { id: string; name: string; }

export default function LeaveEncashment() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager', 'finance'].includes(user?.role ?? '');

  const [requests, setRequests] = useState<Encashment[]>([]);
  const [pending, setPending] = useState<Encashment[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [tab, setTab] = useState<'mine' | 'pending'>('mine');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<any>({ year: new Date().getFullYear(), daysRequested: 1, currency: 'HKD', reason: '' });

  const [reviewDlg, setReviewDlg] = useState<{ id: string; action: 'approve' | 'reject' | 'paid'; note: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [reqs, pend, st, lt] = await Promise.all([
        apiFetch('/api/leave-encashments'),
        isHR ? apiFetch('/api/leave-encashments/pending') : Promise.resolve([]),
        apiFetch('/api/leave-encashments/stats'),
        apiFetch('/api/leave/types').catch(() => []),
      ]);
      setRequests(reqs || []); setPending(pend || []); setStats(st);
      setLeaveTypes(Array.isArray(lt) ? lt : lt.data || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [isHR]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.leaveTypeId) return;
    const lt = leaveTypes.find(l => l.id === form.leaveTypeId);
    try {
      await apiFetch('/api/leave-encashments', {
        method: 'POST',
        body: JSON.stringify({ ...form, leaveTypeName: lt?.name ?? 'Annual Leave' }),
      });
      setFormOpen(false); setForm({ year: new Date().getFullYear(), daysRequested: 1, currency: 'HKD', reason: '' }); load();
    } catch (e: any) { setError(e.message); }
  };

  const review = async () => {
    if (!reviewDlg) return;
    const endpoint = reviewDlg.action === 'paid' ? 'paid' : reviewDlg.action;
    try {
      await apiFetch(`/api/leave-encashments/${reviewDlg.id}/${endpoint}`, {
        method: 'PATCH', body: JSON.stringify({ approverNote: reviewDlg.note }),
      });
      setReviewDlg(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const cancel = async (id: string) => {
    try { await apiFetch(`/api/leave-encashments/${id}/cancel`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const list = tab === 'mine' ? requests : pending;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Savings sx={{ fontSize: 32, color: '#2e7d32' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Leave Encashment</Typography>
            <Typography variant="body2" color="text.secondary">Convert unused leave balance to cash payment</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#2e7d32' }} onClick={() => setFormOpen(true)}>
          Request Encashment
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Requests',   value: stats.total,    color: '#37474f' },
            { label: 'Pending',          value: stats.pending,  color: '#e65100' },
            { label: 'Approved',         value: stats.approved, color: '#1565c0' },
            { label: 'Paid Out',         value: stats.paid,     color: '#2e7d32' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {stats && (stats.totalDaysPaid > 0 || stats.totalAmountPaid > 0) && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#e8f5e9', border: '1px solid #a5d6a7' }}>
          <Typography fontWeight={700} color="#2e7d32">
            💰 YTD Encashment: {stats.totalDaysPaid} days encashed · {fmtHKD(stats.totalAmountPaid)} paid out
          </Typography>
        </Paper>
      )}

      {/* Tab toggle */}
      {isHR && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip label={`My Requests (${requests.length})`} onClick={() => setTab('mine')}
            color={tab === 'mine' ? 'primary' : 'default'} variant={tab === 'mine' ? 'filled' : 'outlined'} />
          <Chip label={`Pending Approval (${pending.length})`} onClick={() => setTab('pending')}
            color={tab === 'pending' ? 'warning' : 'default'} variant={tab === 'pending' ? 'filled' : 'outlined'} />
        </Box>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              {isHR && tab === 'pending' && <TableCell><b>Employee</b></TableCell>}
              <TableCell><b>Leave Type</b></TableCell>
              <TableCell><b>Year</b></TableCell>
              <TableCell><b>Days</b></TableCell>
              <TableCell><b>Daily Rate</b></TableCell>
              <TableCell><b>Total Amount</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Note</b></TableCell>
              <TableCell><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map(r => (
              <TableRow key={r.id} hover>
                {isHR && tab === 'pending' && (
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.employee?.firstName} {r.employee?.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.employee?.department}</Typography>
                  </TableCell>
                )}
                <TableCell>{r.leaveTypeName}</TableCell>
                <TableCell>{r.year}</TableCell>
                <TableCell><b>{r.daysRequested}</b></TableCell>
                <TableCell>{fmtHKD(r.dailyRate)}</TableCell>
                <TableCell sx={{ color: '#2e7d32', fontWeight: 700 }}>{fmtHKD(r.totalAmount)}</TableCell>
                <TableCell><Chip label={STATUS_CFG[r.status]?.label} size="small" color={STATUS_CFG[r.status]?.color} /></TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 150, display: 'block' }}>
                    {r.approverNote ?? r.reason ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {isHR && tab === 'pending' && r.status === 'pending' && (
                      <>
                        <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => setReviewDlg({ id: r.id, action: 'approve', note: '' })}><CheckCircle fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => setReviewDlg({ id: r.id, action: 'reject', note: '' })}><Cancel fontSize="small" /></IconButton></Tooltip>
                      </>
                    )}
                    {isHR && r.status === 'approved' && (
                      <Tooltip title="Mark Paid"><IconButton size="small" color="success" onClick={() => setReviewDlg({ id: r.id, action: 'paid', note: '' })}><Paid fontSize="small" /></IconButton></Tooltip>
                    )}
                    {tab === 'mine' && r.status === 'pending' && (
                      <Tooltip title="Cancel"><IconButton size="small" onClick={() => cancel(r.id)}><Close fontSize="small" /></IconButton></Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && !loading && (
              <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                {tab === 'pending' ? 'No pending requests.' : 'No encashment requests yet.'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── REQUEST FORM ── */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Request Leave Encashment
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setFormOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Leave Type" select fullWidth required value={form.leaveTypeId ?? ''}
              onChange={e => setForm((p: any) => ({ ...p, leaveTypeId: e.target.value }))}>
              {leaveTypes.map(lt => <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>)}
              {leaveTypes.length === 0 && <MenuItem value="">Loading…</MenuItem>}
            </TextField>
            <TextField label="Year" select fullWidth value={form.year}
              onChange={e => setForm((p: any) => ({ ...p, year: Number(e.target.value) }))}>
              {[2024, 2025, 2026].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            <TextField label="Days to Encash" type="number" fullWidth required
              inputProps={{ min: 0.5, max: 30, step: 0.5 }}
              value={form.daysRequested}
              onChange={e => setForm((p: any) => ({ ...p, daysRequested: Number(e.target.value) }))} />
            <TextField label="Reason (optional)" fullWidth multiline rows={2} value={form.reason}
              onChange={e => setForm((p: any) => ({ ...p, reason: e.target.value }))} />
            <Typography variant="caption" color="text.secondary">
              💡 Daily rate will be computed from your latest payroll record. Finance will confirm final amount on approval.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#2e7d32' }} onClick={submit}
            disabled={!form.leaveTypeId || !form.daysRequested}>
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── REVIEW DIALOG ── */}
      <Dialog open={!!reviewDlg} onClose={() => setReviewDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {reviewDlg?.action === 'approve' ? '✅ Approve Encashment' :
           reviewDlg?.action === 'reject'  ? '❌ Reject Encashment' : '💰 Mark as Paid'}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label={reviewDlg?.action === 'paid' ? 'Payroll reference (optional)' : reviewDlg?.action === 'reject' ? 'Rejection reason *' : 'Note (optional)'}
            fullWidth value={reviewDlg?.note ?? ''}
            onChange={e => setReviewDlg(p => p ? { ...p, note: e.target.value } : null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDlg(null)}>Cancel</Button>
          <Button variant="contained"
            color={reviewDlg?.action === 'reject' ? 'error' : 'success'}
            disabled={reviewDlg?.action === 'reject' && !reviewDlg?.note?.trim()}
            onClick={review}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
